import { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Activity, Flame, Layers, Timer } from "lucide-react";
import { useAlertProfile, useAnalyze } from "../../lib/queries";
import { KpiTile } from "../../components/KpiTile";
import ExportMenu from "../../components/ExportMenu";
import {
  EmptyState,
  GlassCard,
  SectionTag,
  SeverityCapsule,
  Spinner,
} from "../../components/ui";
import { EMESRT_COLOR, fmtNum, SEVERITY_COLOR, SEVERITY_LABEL } from "../../lib/format";
import type { Severity } from "../../types/api";

const SEVERITY_SERIES: Severity[] = ["critical", "high", "medium", "low", "advisory"];

// Aurora → pacific cycling palette for the type donut / generic categorical use.
const AURORA_PALETTE = ["#22D3EE", "#3B82F6", "#2DD4BF", "#818CF8", "#38BDF8", "#0EA5E9"];

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const TOOLTIP_STYLE = {
  background: "#0D1F33",
  border: "1px solid rgba(148,163,184,0.2)",
  borderRadius: 8,
  fontSize: 12,
} as const;

const AXIS_TICK = { fill: "#64748B", fontSize: 10 } as const;
const GRID_STROKE = "rgba(148,163,184,0.08)";

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
}

export default function Analyze() {
  const { data, isLoading } = useAnalyze();
  const { data: profile, isLoading: profileLoading } = useAlertProfile();
  const [activeType, setActiveType] = useState<string | null>(null);

  const total = data?.total ?? 0;
  const prevTotal = data?.prev_total ?? 0;
  const byType = useMemo(() => data?.by_type ?? [], [data]);
  const bySeverity = useMemo(() => data?.by_severity ?? [], [data]);
  const byEmesrt = useMemo(() => data?.by_emesrt ?? [], [data]);
  const leaderboard = useMemo(() => data?.leaderboard ?? [], [data]);
  const dailyTrend = useMemo(() => data?.daily_trend ?? [], [data]);
  const heatmap = useMemo(() => data?.heatmap ?? [], [data]);

  // KPI derivations -------------------------------------------------------
  const totalDelta = prevTotal ? round1(((total - prevTotal) / prevTotal) * 100) : null;
  const trendSpark = useMemo(() => dailyTrend.map((d) => d.count), [dailyTrend]);

  const criticalCount = useMemo(
    () =>
      bySeverity
        .filter((s) => s.severity === "high" || s.severity === "critical")
        .reduce((acc, s) => acc + s.count, 0),
    [bySeverity]
  );
  const criticalShare = total ? round1((criticalCount / total) * 100) : 0;

  const avgFpRate = useMemo(() => {
    if (dailyTrend.length === 0) return 0;
    const sum = dailyTrend.reduce((acc, d) => acc + d.fp_rate, 0);
    return round1((sum / dailyTrend.length) * 100);
  }, [dailyTrend]);

  // Heatmap lookup --------------------------------------------------------
  const heatLookup = useMemo(() => {
    const map = new Map<string, number>();
    for (const cell of heatmap) map.set(`${cell.weekday}-${cell.hour}`, cell.count);
    return map;
  }, [heatmap]);
  const heatMax = useMemo(() => heatmap.reduce((m, c) => Math.max(m, c.count), 0), [heatmap]);

  // Chart-ready datasets --------------------------------------------------
  const trendRows = useMemo(
    () => dailyTrend.map((d) => ({ ...d, label: shortDate(d.date) })),
    [dailyTrend]
  );
  const profileRows = useMemo(
    () =>
      (profile?.rows ?? []).map((b) => ({
        ...b,
        t: new Date(b.bucket).toLocaleString(
          "en-AU",
          profile?.granularity === "day"
            ? { day: "2-digit", month: "short" }
            : { day: "2-digit", month: "short", hour: "2-digit" }
        ),
      })),
    [profile]
  );
  const leaderboardRows = useMemo(() => leaderboard.slice(0, 12), [leaderboard]);
  const emesrtMax = useMemo(() => byEmesrt.reduce((m, e) => Math.max(m, e.count), 0), [byEmesrt]);

  if (isLoading) {
    return (
      <div className="space-y-5">
        <Header />
        <GlassCard>
          <Spinner label="Crunching exception intelligence…" />
        </GlassCard>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="space-y-5">
        <Header />
        <GlassCard>
          <EmptyState>No exceptions recorded in the selected range.</EmptyState>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Header />

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiTile
          label="TOTAL EXCEPTIONS"
          value={fmtNum(total)}
          deltaPct={totalDelta}
          invertDelta
          spark={trendSpark}
          accent="var(--aurora-cyan)"
        />
        <KpiTile
          label="CRITICAL SHARE"
          value={`${criticalShare}%`}
          critical
          spark={dailyTrend.map((d) => d.critical)}
        />
        <KpiTile
          label="TIME-TO-TRIAGE"
          value={`${data?.mttr_minutes ?? 0}m`}
          accent="var(--pacific)"
        />
        <KpiTile label="AVG FP-RATE" value={`${avgFpRate}%`} accent="var(--aurora-teal)" />
      </div>

      {/* Daily trend + alert profile */}
      <div className="grid gap-3 lg:grid-cols-2">
        <GlassCard>
          <div className="mb-2 flex items-center gap-2">
            <Activity size={14} className="text-pacific" />
            <SectionTag>DAILY TREND · VOLUME &amp; CRITICAL</SectionTag>
          </div>
          {trendRows.length === 0 ? (
            <EmptyState>No daily activity.</EmptyState>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={trendRows} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="trend-count" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="label" tick={AXIS_TICK} interval="preserveStartEnd" />
                <YAxis tick={AXIS_TICK} allowDecimals={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area
                  type="monotone"
                  dataKey="count"
                  name="Exceptions"
                  stroke="#3B82F6"
                  fill="url(#trend-count)"
                  strokeWidth={1.5}
                />
                <Line
                  type="monotone"
                  dataKey="critical"
                  name="Critical"
                  stroke="#EF4444"
                  strokeWidth={1.75}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </GlassCard>

        <GlassCard>
          <SectionTag>ALERT PROFILE · BY SEVERITY</SectionTag>
          {profileLoading ? (
            <div className="h-[260px] p-6">
              <Spinner />
            </div>
          ) : profileRows.length === 0 ? (
            <EmptyState>No alert activity in window.</EmptyState>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={profileRows} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  {SEVERITY_SERIES.map((s) => (
                    <linearGradient key={s} id={`prof-${s}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={SEVERITY_COLOR[s]} stopOpacity={0.55} />
                      <stop offset="100%" stopColor={SEVERITY_COLOR[s]} stopOpacity={0.05} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="t" tick={AXIS_TICK} interval="preserveStartEnd" />
                <YAxis tick={AXIS_TICK} allowDecimals={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {SEVERITY_SERIES.map((s) => (
                  <Area
                    key={s}
                    type="monotone"
                    dataKey={s}
                    name={SEVERITY_LABEL[s]}
                    stackId="1"
                    stroke={SEVERITY_COLOR[s]}
                    fill={`url(#prof-${s})`}
                    strokeWidth={1.5}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </GlassCard>
      </div>

      {/* Type donut + severity donut */}
      <div className="grid gap-3 lg:grid-cols-2">
        <GlassCard>
          <div className="mb-2 flex items-center gap-2">
            <Layers size={14} className="text-aurora-cyan" />
            <SectionTag>EXCEPTIONS · BY TYPE</SectionTag>
          </div>
          {byType.length === 0 ? (
            <EmptyState>No typed exceptions.</EmptyState>
          ) : (
            <div className="flex flex-col items-center gap-4 sm:flex-row">
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={byType}
                    dataKey="count"
                    nameKey="event_type__label"
                    cx="50%"
                    cy="50%"
                    innerRadius={58}
                    outerRadius={94}
                    paddingAngle={2}
                    stroke="none"
                  >
                    {byType.map((t, i) => (
                      <Cell
                        key={t.event_type__slug}
                        fill={AURORA_PALETTE[i % AURORA_PALETTE.length]}
                        opacity={activeType && activeType !== t.event_type__slug ? 0.3 : 1}
                      />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex max-h-[240px] shrink-0 flex-col gap-1.5 overflow-y-auto pr-1">
                {byType.map((t, i) => (
                  <button
                    key={t.event_type__slug}
                    onMouseEnter={() => setActiveType(t.event_type__slug)}
                    onMouseLeave={() => setActiveType(null)}
                    className="flex items-center gap-2 text-left text-xs transition hover:opacity-100"
                    style={{ opacity: activeType && activeType !== t.event_type__slug ? 0.4 : 1 }}
                  >
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-sm"
                      style={{ background: AURORA_PALETTE[i % AURORA_PALETTE.length] }}
                    />
                    <span className="w-32 truncate text-slatey">{t.event_type__label}</span>
                    <span className="mono ml-auto text-ink">{fmtNum(t.count)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </GlassCard>

        <GlassCard>
          <div className="mb-2 flex items-center gap-2">
            <Flame size={14} className="text-critical" />
            <SectionTag>SEVERITY · DISTRIBUTION</SectionTag>
          </div>
          {bySeverity.length === 0 ? (
            <EmptyState>No severity data.</EmptyState>
          ) : (
            <div className="flex flex-col items-center gap-4 sm:flex-row">
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={bySeverity}
                    dataKey="count"
                    nameKey="severity"
                    cx="50%"
                    cy="50%"
                    innerRadius={58}
                    outerRadius={94}
                    paddingAngle={2}
                    stroke="none"
                  >
                    {bySeverity.map((s) => (
                      <Cell key={s.severity} fill={SEVERITY_COLOR[s.severity]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex shrink-0 flex-col gap-2">
                {bySeverity.map((s) => (
                  <div key={s.severity} className="flex items-center gap-2 text-xs">
                    <SeverityCapsule severity={s.severity} />
                    <span className="mono ml-auto text-ink">{fmtNum(s.count)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </GlassCard>
      </div>

      {/* Severity heatmap (custom grid) */}
      <GlassCard>
        <SectionTag>ACTIVITY HEATMAP · HOUR × WEEKDAY</SectionTag>
        {heatMax === 0 ? (
          <EmptyState>No heatmap activity.</EmptyState>
        ) : (
          <div className="overflow-x-auto pt-1">
            <div className="min-w-[640px]">
              {/* Hour axis */}
              <div className="mb-1 flex pl-10">
                {Array.from({ length: 24 }, (_, h) => (
                  <div key={h} className="flex-1 text-center text-[9px] text-slatey">
                    {h % 3 === 0 ? h : ""}
                  </div>
                ))}
              </div>
              {WEEKDAYS.map((day, di) => {
                const weekday = di + 1; // 1=Mon..7=Sun
                return (
                  <div key={day} className="mb-1 flex items-center">
                    <div className="w-10 shrink-0 pr-2 text-right text-[10px] text-slatey">
                      {day}
                    </div>
                    <div className="flex flex-1 gap-[2px]">
                      {Array.from({ length: 24 }, (_, hour) => {
                        const count = heatLookup.get(`${weekday}-${hour}`) ?? 0;
                        const alpha = count === 0 ? 0 : 0.12 + (count / heatMax) * 0.85;
                        return (
                          <div
                            key={hour}
                            title={`${day} ${hour}:00 — ${count} event${count === 1 ? "" : "s"}`}
                            className="aspect-square flex-1 rounded-[3px] border border-white/5"
                            style={{
                              background:
                                count === 0
                                  ? "rgba(148,163,184,0.04)"
                                  : `rgba(34,211,238,${alpha.toFixed(3)})`,
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {/* Intensity legend */}
              <div className="mt-3 flex items-center gap-2 pl-10 text-[10px] text-slatey">
                <span>less</span>
                {[0.12, 0.35, 0.58, 0.81, 1].map((a) => (
                  <span
                    key={a}
                    className="h-3 w-5 rounded-[3px]"
                    style={{ background: `rgba(34,211,238,${a})` }}
                  />
                ))}
                <span>more</span>
                <span className="mono ml-auto">peak {fmtNum(heatMax)}/cell</span>
              </div>
            </div>
          </div>
        )}
      </GlassCard>

      {/* Site leaderboard */}
      <GlassCard>
        <SectionTag>SITE LEADERBOARD · EXCEPTIONS &amp; CRITICAL</SectionTag>
        {leaderboardRows.length === 0 ? (
          <EmptyState>No site data.</EmptyState>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(280, leaderboardRows.length * 30)}>
            <BarChart
              layout="vertical"
              data={leaderboardRows}
              margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
            >
              <CartesianGrid horizontal={false} stroke={GRID_STROKE} />
              <XAxis type="number" tick={AXIS_TICK} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="site__title"
                tick={AXIS_TICK}
                width={140}
                interval={0}
              />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "rgba(148,163,184,0.06)" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="count" name="Exceptions" fill="#3B82F6" radius={[0, 4, 4, 0]} />
              <Bar dataKey="critical" name="Critical" fill="#EF4444" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </GlassCard>

      {/* EMESRT distribution */}
      <GlassCard>
        <SectionTag>EMESRT · INTERACTION LAYER DISTRIBUTION</SectionTag>
        {byEmesrt.length === 0 ? (
          <EmptyState>No EMESRT data.</EmptyState>
        ) : (
          <div className="space-y-3 pt-1">
            {byEmesrt.map((e) => {
              const level = e.event_type__emesrt_level;
              const color = EMESRT_COLOR[level] ?? "#64748B";
              const width = emesrtMax ? (e.count / emesrtMax) * 100 : 0;
              return (
                <div key={level} className="flex items-center gap-2 text-xs">
                  <span
                    className="mono w-10 shrink-0 rounded px-1 text-center"
                    style={{ color, background: `${color}1a` }}
                  >
                    {level || "—"}
                  </span>
                  <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-white/5">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${width}%`, background: color }}
                    />
                  </div>
                  <span className="mono w-10 text-right text-ink">{fmtNum(e.count)}</span>
                </div>
              );
            })}
          </div>
        )}
      </GlassCard>
    </div>
  );
}

function Header() {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <SectionTag>ANALYZE · EXCEPTION INTELLIGENCE</SectionTag>
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <Timer size={18} className="text-aurora-cyan" />
          Analytics Workspace
        </h1>
      </div>
      <ExportMenu title="Analytics export" />
    </div>
  );
}
